import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  EmojiEvents as RankingIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  School as LearningIcon,
  WorkspacePremium as CertificateIcon,
  Description as ReportsIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlayerSidebar from '@components/layout/PlayerSidebar';

const DRAWER_WIDTH = 280;
const CATEGORY_LABELS: Record<string, string> = {
  'self-evaluation': 'Self Evaluation',
  'coach-academy': 'Coach/Academy Evaluation',
  'practical-test': 'Board Exam',
};

const TRAINING_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'high-level', label: 'High-level (skill)' },
  { value: 'low-level', label: 'Low-level (skill)' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'drill', label: 'Drill' },
];

interface Peer {
  id: string;
  name: string;
}

interface ReportSummary {
  reportId: string;
  playerId: string;
  playerName: string;
  score: number;
  grade: string;
  percentage: string;
  periodKey: string;
}

function strokeDisplayLabel(s: { courtPosition?: string; halfSide?: string; strokeType?: string; strokeAction?: string; shotType?: string; placement?: string; code?: string }): string {
  const parts = [s.courtPosition, s.halfSide, s.strokeType, s.strokeAction, s.shotType, s.placement].filter((v) => v != null && String(v).trim() !== '');
  return parts.length > 0 ? parts.join(' · ') : (s.code || '');
}

interface StrokeWithMeta {
  code: string;
  rating: number | null;
  displayLabel: string;
}

interface RankingReportDetail {
  reportId: string;
  playerName: string;
  strokes: StrokeWithMeta[];
}

const ComparePeersPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [player, setPlayer] = useState<any>(null);
  const [peers, setPeers] = useState<Peer[]>([]);
  const [myReports, setMyReports] = useState<any[]>([]);
  const [periodKey, setPeriodKey] = useState<string>('');
  const [sport, setSport] = useState<string>('Badminton');
  const [categoryId, setCategoryId] = useState<string>('coach-academy');
  const [comparison, setComparison] = useState<ReportSummary[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compareTab, setCompareTab] = useState<'training' | 'ranking'>('ranking');
  const [trainingPeriodKey, setTrainingPeriodKey] = useState<string>('');
  const [trainingPeriods, setTrainingPeriods] = useState<{ periodKey: string; label: string }[]>([]);
  const [trainingComparison, setTrainingComparison] = useState<{ playerId: string; playerName: string; averageRating: number; evaluationId: string }[]>([]);
  const [loadingTrainingCompare, setLoadingTrainingCompare] = useState(false);
  const [trainingCategory, setTrainingCategory] = useState<string>('high-level');
  const [trainingDetailCache, setTrainingDetailCache] = useState<Record<string, any>>({});
  const [selectedForBenchmark, setSelectedForBenchmark] = useState<string[]>([]);
  const [reportViewDialog, setReportViewDialog] = useState<'ranking' | 'training' | null>(null);
  const [reportViewPayload, setReportViewPayload] = useState<{ reportId?: string; evaluationId?: string; playerId: string; playerName: string } | null>(null);
  const [rankingDetailCache, setRankingDetailCache] = useState<Record<string, RankingReportDetail>>({});
  const [loadingRankingDetails, setLoadingRankingDetails] = useState<Record<string, boolean>>({});
  const [onlyStrokesWithDelta, setOnlyStrokesWithDelta] = useState(false);
  const [selectedForTrainingBenchmark, setSelectedForTrainingBenchmark] = useState<string[]>([]);
  const [trainingOnlyStrokesWithDelta, setTrainingOnlyStrokesWithDelta] = useState(false);

  useEffect(() => {
    loadPlayerDetails();
  }, [user?.id]);

  useEffect(() => {
    if (player?.id) {
      loadPeers();
      loadMyReports();
    }
  }, [player?.id]);

  const loadTrainingPeriods = async () => {
    if (!player?.id || !sport) return;
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(
        `/api/performance/basic/player/${player.id}?sport=${sport}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const submitted = data.data.filter((e: any) => e.submitted && (e.averageRating != null || e.averageRating === 0) && matchesTrainingCategory(e));
        const periodSet = new Map<string, string>();
        submitted.forEach((e: any) => {
          const p = e.period || '';
          const year = e.dateRange?.start ? new Date(e.dateRange.start).getFullYear() : new Date().getFullYear();
          const key = `${p}-${year}`;
          if (key && key !== '-') periodSet.set(key, p.startsWith('month-') ? `${p.replace('month-', '')}/${year}` : `${p} ${year}`);
        });
        const list = Array.from(periodSet.entries()).map(([k, label]) => ({ periodKey: k, label })).sort((a, b) => b.periodKey.localeCompare(a.periodKey));
        setTrainingPeriods(list);
        if (list.length > 0 && (!trainingPeriodKey || !list.some((p) => p.periodKey === trainingPeriodKey))) {
          setTrainingPeriodKey(list[0].periodKey);
        }
      } else {
        setTrainingPeriods([]);
      }
    } catch {
      setTrainingPeriods([]);
    }
  };

  const matchesTrainingCategory = (e: any): boolean => {
    if (trainingCategory === 'all') return true;
    const cat = (e.category || e.assessmentType || '').toLowerCase();
    if (trainingCategory === 'high-level') return ['high-level', 'quick', 'basic'].includes(cat);
    if (trainingCategory === 'low-level') return ['low-level', 'advanced'].includes(cat);
    if (trainingCategory === 'fitness') return cat === 'fitness';
    if (trainingCategory === 'drill') return cat === 'drill';
    return true;
  };

  useEffect(() => {
    if (compareTab === 'training' && player?.id && sport) {
      loadTrainingPeriods();
    }
  }, [compareTab, player?.id, sport, trainingCategory]);

  const loadPlayerDetails = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/players/me?userId=${user.id}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await response.json();
      if (data.success && data.data) {
        setPlayer(data.data);
      } else {
        setError(data.message || 'Failed to load profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPeers = async () => {
    if (!player?.id) return;
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/ranking-reports/player/${player.id}/peers`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        cache: 'no-store',
      });
      const data = await response.json();
      if (response.ok && data.success && Array.isArray(data.data)) {
        setPeers(data.data.map((p: any) => ({ id: p.id, name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Peer' })));
      } else {
        setPeers([]);
      }
    } catch {
      setPeers([]);
    }
  };

  const loadMyReports = async () => {
    if (!player?.id) return;
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(
        `/api/ranking-reports/player/${player.id}?sport=${sport}&categoryId=${categoryId}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, cache: 'no-store' }
      );
      const data = await response.json();
      if (response.ok && data.success && Array.isArray(data.data)) {
        setMyReports(data.data);
        if (data.data.length > 0 && !periodKey) {
          setPeriodKey(data.data[0].periodKey || '');
        }
      } else {
        setMyReports([]);
      }
    } catch {
      setMyReports([]);
    }
  };

  useEffect(() => {
    if (player?.id && sport && categoryId) {
      loadMyReports();
    }
  }, [player?.id, sport, categoryId]);

  const loadTrainingComparison = async () => {
    if (!player?.id || !sport || !trainingPeriodKey) return;
    setLoadingTrainingCompare(true);
    setTrainingComparison([]);
    setSelectedForTrainingBenchmark([]);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const results: { playerId: string; playerName: string; averageRating: number; evaluationId: string }[] = [];
      const myName = [player.firstName, player.lastName].filter(Boolean).join(' ') || 'Me';

      const fetchPlayerTraining = async (pid: string): Promise<{ averageRating: number; evaluationId: string; fullEvaluation?: any } | null> => {
        const res = await fetch(`/api/performance/basic/player/${pid}?sport=${sport}`, { headers });
        const json = await res.json();
        if (!json.success || !Array.isArray(json.data)) return null;
        const submitted = json.data.filter((e: any) => e.submitted && (e.averageRating != null || e.averageRating === 0) && matchesTrainingCategory(e));
        const match = submitted.find((e: any) => {
          const p = e.period || '';
          const y = e.dateRange?.start ? new Date(e.dateRange.start).getFullYear() : new Date().getFullYear();
          const key = `${p}-${y}`;
          return key === trainingPeriodKey;
        });
        if (match && (match.averageRating != null || match.averageRating === 0)) {
          if (match.id) {
            setTrainingDetailCache((prev) => ({ ...prev, [match.id]: { ...match, playerId: pid } }));
          }
          return { averageRating: Number(match.averageRating), evaluationId: match.id, fullEvaluation: match };
        }
        return null;
      };

      const myResult = await fetchPlayerTraining(player.id);
      if (myResult) {
        results.push({ playerId: player.id, playerName: myName, averageRating: myResult.averageRating, evaluationId: myResult.evaluationId });
      }
      for (const peer of peers) {
        const peerResult = await fetchPlayerTraining(peer.id);
        if (peerResult) {
          results.push({ playerId: peer.id, playerName: peer.name, averageRating: peerResult.averageRating, evaluationId: peerResult.evaluationId });
        }
      }
      results.sort((a, b) => b.averageRating - a.averageRating);
      setTrainingComparison(results);
    } catch (err) {
      console.error('Training comparison error:', err);
    } finally {
      setLoadingTrainingCompare(false);
    }
  };

  const loadComparison = async () => {
    if (!player?.id || !periodKey) return;
    setLoadingCompare(true);
    setComparison([]);
    setSelectedForBenchmark([]);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const results: ReportSummary[] = [];
      const myName = [player.firstName, player.lastName].filter(Boolean).join(' ') || 'Me';

      const myListRes = await fetch(
        `/api/ranking-reports/player/${player.id}?sport=${sport}&categoryId=${categoryId}`,
        { headers }
      );
      const myList = await myListRes.json();
      const myReport = myList.success && Array.isArray(myList.data)
        ? myList.data.find((r: any) => r.periodKey === periodKey)
        : null;
      if (myReport) {
        results.push({
          reportId: myReport.reportId,
          playerId: player.id,
          playerName: myName,
          score: myReport.score ?? 0,
          grade: myReport.grade || '–',
          percentage: myReport.percentage || '0',
          periodKey: myReport.periodKey,
        });
      }

      for (const peer of peers) {
        const peerRes = await fetch(
          `/api/ranking-reports/player/${peer.id}?sport=${sport}&categoryId=${categoryId}`,
          { headers }
        );
        const peerData = await peerRes.json();
        const peerReport = peerData.success && Array.isArray(peerData.data)
          ? peerData.data.find((r: any) => r.periodKey === periodKey)
          : null;
        if (peerReport) {
          results.push({
            reportId: peerReport.reportId,
            playerId: peer.id,
            playerName: peer.name,
            score: peerReport.score ?? 0,
            grade: peerReport.grade || '–',
            percentage: peerReport.percentage || '0',
            periodKey: peerReport.periodKey,
          });
        }
      }

      results.sort((a, b) => b.score - a.score);
      setComparison(results);
    } catch (err) {
      console.error('Comparison load error:', err);
    } finally {
      setLoadingCompare(false);
    }
  };

  const toggleTrainingBenchmark = (evaluationId: string) => {
    setSelectedForTrainingBenchmark((prev) =>
      prev.includes(evaluationId) ? prev.filter((id) => id !== evaluationId) : prev.length >= 2 ? [...prev.slice(1), evaluationId] : [...prev, evaluationId]
    );
  };

  const openRankingReportView = (reportId: string, playerId: string, playerName: string) => {
    setReportViewPayload({ reportId, playerId, playerName });
    setReportViewDialog('ranking');
  };
  const openTrainingReportView = (evaluationId: string, playerId: string, playerName: string) => {
    setReportViewPayload({ evaluationId, playerId, playerName });
    setReportViewDialog('training');
  };
  const closeReportView = () => {
    setReportViewDialog(null);
    setReportViewPayload(null);
  };

  const toggleBenchmark = (reportId: string) => {
    setSelectedForBenchmark((prev) =>
      prev.includes(reportId) ? prev.filter((id) => id !== reportId) : prev.length >= 2 ? [...prev.slice(1), reportId] : [...prev, reportId]
    );
  };

  const loadReportDetail = async (reportId: string, playerName: string) => {
    if (rankingDetailCache[reportId]) return;
    setLoadingRankingDetails((prev) => ({ ...prev, [reportId]: true }));
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/ranking-reports/${encodeURIComponent(reportId)}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const result = await response.json();
      if (result.success && result.data) {
        const strokes: StrokeWithMeta[] = (result.data.strokes || []).map((s: any) => ({
          code: s.code || '',
          rating: s.rating != null ? Number(s.rating) : null,
          displayLabel: strokeDisplayLabel({
            courtPosition: s.courtPosition,
            halfSide: s.halfSide,
            strokeType: s.strokeType,
            strokeAction: s.strokeAction,
            shotType: s.shotType,
            placement: s.placement,
            code: s.code,
          }),
        }));
        setRankingDetailCache((prev) => ({
          ...prev,
          [reportId]: { reportId, playerName, strokes },
        }));
      }
    } catch (e) {
      console.error('Error loading report detail:', e);
    } finally {
      setLoadingRankingDetails((prev) => ({ ...prev, [reportId]: false }));
    }
  };

  useEffect(() => {
    if (selectedForBenchmark.length === 0) return;
    const namesByReportId: Record<string, string> = {};
    comparison.forEach((r) => {
      namesByReportId[r.reportId] = r.playerName + (r.playerId === player?.id ? ' (You)' : '');
    });
    selectedForBenchmark.forEach((id) => loadReportDetail(id, namesByReportId[id] || ''));
  }, [selectedForBenchmark.join(','), comparison.length]);

  useEffect(() => {
    if (reportViewDialog === 'ranking' && reportViewPayload?.reportId && reportViewPayload?.playerName && !rankingDetailCache[reportViewPayload.reportId]) {
      loadReportDetail(reportViewPayload.reportId, reportViewPayload.playerName);
    }
  }, [reportViewDialog, reportViewPayload?.reportId, reportViewPayload?.playerName]);

  const selectedBenchmarkReports = useMemo(() => comparison.filter((r) => selectedForBenchmark.includes(r.reportId)), [comparison, selectedForBenchmark]);
  const benchmarkDetails = useMemo(
    () => selectedBenchmarkReports.map((r) => rankingDetailCache[r.reportId]).filter(Boolean) as RankingReportDetail[],
    [selectedBenchmarkReports, rankingDetailCache]
  );
  const benchmarkComparisonData = useMemo(() => {
    if (benchmarkDetails.length !== 2) return { chartData: [], tableRows: [] };
    const [d0, d1] = benchmarkDetails;
    const byCode: Record<string, { code: string; displayLabel: string; ratings: (number | null)[] }> = {};
    [d0, d1].forEach((d, idx) => {
      d.strokes.forEach((s) => {
        if (!byCode[s.code]) {
          byCode[s.code] = { code: s.code, displayLabel: s.displayLabel || s.code, ratings: [null, null] };
        }
        if (s.displayLabel?.trim()) byCode[s.code].displayLabel = s.displayLabel;
        byCode[s.code].ratings[idx] = s.rating;
      });
    });
    const tableRows = Object.values(byCode).sort((a, b) => a.code.localeCompare(b.code));
    const chartData = tableRows.map((row, i) => ({
      index: i + 1,
      fullLabel: row.displayLabel || row.code,
      r0: row.ratings[0] != null ? row.ratings[0] : 0,
      r1: row.ratings[1] != null ? row.ratings[1] : 0,
    }));
    return { chartData, tableRows };
  }, [benchmarkDetails]);
  const hasDelta = (ratings: (number | null)[]) => {
    const values = ratings.filter((r) => r != null) as number[];
    return new Set(values).size > 1;
  };
  const { chartData: displayChartData, tableRows: displayTableRows } = useMemo(() => {
    if (!onlyStrokesWithDelta) return { chartData: benchmarkComparisonData.chartData, tableRows: benchmarkComparisonData.tableRows };
    const filtered = benchmarkComparisonData.tableRows.filter((row) => hasDelta(row.ratings));
    const chartData = filtered.map((row, i) => ({
      index: i + 1,
      fullLabel: row.displayLabel || row.code,
      r0: row.ratings[0] != null ? row.ratings[0] : 0,
      r1: row.ratings[1] != null ? row.ratings[1] : 0,
    }));
    return { chartData, tableRows: filtered };
  }, [benchmarkComparisonData, onlyStrokesWithDelta]);

  const selectedTrainingBenchmarkRows = useMemo(
    () => trainingComparison.filter((r) => selectedForTrainingBenchmark.includes(r.evaluationId)),
    [trainingComparison, selectedForTrainingBenchmark]
  );
  const trainingBenchmarkDetails = useMemo(() => {
    return selectedTrainingBenchmarkRows
      .map((r) => {
        const d = trainingDetailCache[r.evaluationId];
        if (!d || !d.items) return null;
        return { playerName: r.playerName + (r.playerId === player?.id ? ' (You)' : ''), items: d.items };
      })
      .filter(Boolean) as { playerName: string; items: any[] }[];
  }, [selectedTrainingBenchmarkRows, trainingDetailCache, player?.id]);
  const trainingBenchmarkComparisonData = useMemo(() => {
    if (trainingBenchmarkDetails.length !== 2) return { chartData: [], tableRows: [] };
    const [d0, d1] = trainingBenchmarkDetails;
    const key = (item: any) => `${item.category || ''}|${item.name || ''}`;
    const byKey: Record<string, { category: string; name: string; ratings: (number | null)[] }> = {};
    [d0, d1].forEach((d, idx) => {
      (d.items || []).forEach((item: any) => {
        const k = key(item);
        if (!byKey[k]) byKey[k] = { category: item.category || '', name: item.name || '', ratings: [null, null] };
        while (byKey[k].ratings.length < 2) byKey[k].ratings.push(null);
        byKey[k].ratings[idx] = item.notApplicable ? null : (item.rating ?? null);
      });
    });
    const tableRows = Object.values(byKey).sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    const chartData = tableRows.map((row, i) => ({
      index: i + 1,
      fullLabel: row.name.length > 30 ? row.name.slice(0, 28) + '…' : row.name,
      r0: row.ratings[0] != null ? row.ratings[0] : 0,
      r1: row.ratings[1] != null ? row.ratings[1] : 0,
    }));
    return { chartData, tableRows };
  }, [trainingBenchmarkDetails]);
  const trainingHasDelta = (ratings: (number | null)[]) => {
    const values = ratings.filter((r) => r != null) as number[];
    return new Set(values).size > 1;
  };
  const { chartData: trainingDisplayChartData, tableRows: trainingDisplayTableRows } = useMemo(() => {
    if (!trainingOnlyStrokesWithDelta) return { chartData: trainingBenchmarkComparisonData.chartData, tableRows: trainingBenchmarkComparisonData.tableRows };
    const filtered = trainingBenchmarkComparisonData.tableRows.filter((row) => trainingHasDelta(row.ratings));
    const chartData = filtered.map((row, i) => ({
      index: i + 1,
      fullLabel: row.name.length > 30 ? row.name.slice(0, 28) + '…' : row.name,
      r0: row.ratings[0] != null ? row.ratings[0] : 0,
      r1: row.ratings[1] != null ? row.ratings[1] : 0,
    }));
    return { chartData, tableRows: filtered };
  }, [trainingBenchmarkComparisonData, trainingOnlyStrokesWithDelta]);

  const handleViewTrainingReport = (evaluationId: string) => {
    navigate(`/player/performance/${sport}/skill-assessment-high-level?evaluationId=${evaluationId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading && !player) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !player) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const drawer = <PlayerSidebar />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>{drawer}</Drawer>
      <Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }} open>{drawer}</Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 2, pt: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 48, md: 48 } }}><IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2, display: { md: 'none' } }}><MenuIcon /></IconButton></Toolbar>
        <Container maxWidth="lg" sx={{ pt: 0 }}>
          <Typography variant="h5" gutterBottom fontWeight={700} sx={{ mt: 0, pt: 0 }}>Peer Benchmark</Typography>
          <Typography color="text.secondary" paragraph sx={{ mt: 0.5 }}>Use the <strong>Ranking</strong> tab to compare two people skill-wise (per stroke) with a line graph and table. Select Sport, Category, Period, then Compare Performance, check two rows, and the graph and skill table will appear below.</Typography>

          <Tabs value={compareTab} onChange={(_: React.SyntheticEvent, v: 'training' | 'ranking') => setCompareTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="Training" value="training" />
            <Tab label="Ranking" value="ranking" />
          </Tabs>

          {peers.length === 0 ? (
            <Alert severity="info">No peers found in your academy. Comparison is available for students in the same academy.</Alert>
          ) : compareTab === 'training' ? (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Sport</InputLabel>
                    <Select value={sport} label="Sport" onChange={(e) => setSport(e.target.value)}>
                      <MenuItem value="Badminton">Badminton</MenuItem>
                      <MenuItem value="Cricket">Cricket</MenuItem>
                      <MenuItem value="Tennis">Tennis</MenuItem>
                      <MenuItem value="Football">Football</MenuItem>
                      <MenuItem value="Athletics">Athletics</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={trainingCategory} label="Category" onChange={(e) => setTrainingCategory(e.target.value)}>
                      {TRAINING_CATEGORY_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Period</InputLabel>
                    <Select value={trainingPeriodKey} label="Period" onChange={(e) => setTrainingPeriodKey(e.target.value)}>
                      {trainingPeriods.map((p) => (<MenuItem key={p.periodKey} value={p.periodKey}>{p.label}</MenuItem>))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={loadTrainingComparison} disabled={loadingTrainingCompare || !trainingPeriodKey}>
                    {loadingTrainingCompare ? <CircularProgress size={24} /> : 'Compare Performance'}
                  </Button>
                </Box>
              </Paper>
              {(trainingComparison.length > 0) && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Select <strong>two</strong> rows below to see <strong>skill-wise comparison</strong> (line graph and table).</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>Compare</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Average rating (out of 5)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trainingComparison.map((row, idx) => (
                          <TableRow key={row.evaluationId} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedForTrainingBenchmark.includes(row.evaluationId)}
                                onChange={() => toggleTrainingBenchmark(row.evaluationId)}
                                disabled={selectedForTrainingBenchmark.length >= 2 && !selectedForTrainingBenchmark.includes(row.evaluationId)}
                              />
                            </TableCell>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.playerName}{row.playerId === player?.id ? ' (You)' : ''}</TableCell>
                            <TableCell>{row.averageRating.toFixed(2)}</TableCell>
                            <TableCell><Button size="small" variant="outlined" onClick={() => openTrainingReportView(row.evaluationId, row.playerId, row.playerName + (row.playerId === player?.id ? ' (You)' : ''))}>View</Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {selectedForTrainingBenchmark.length === 2 && trainingBenchmarkDetails.length === 2 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Skill-wise comparison (Training)</Typography>
                      {trainingDisplayChartData.length > 0 ? (
                        <>
                          <Paper sx={{ p: 2, mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rating by skill</Typography>
                              <FormControlLabel
                                control={<Checkbox checked={trainingOnlyStrokesWithDelta} onChange={(e) => setTrainingOnlyStrokesWithDelta(e.target.checked)} size="small" />}
                                label={<Typography variant="body2">Show only skills with change</Typography>}
                              />
                            </Box>
                            <Box sx={{ bgcolor: '#f5f9fc', borderRadius: 1, p: 1 }}>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={trainingDisplayChartData} margin={{ top: 10, right: 24, left: 8, bottom: 32 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#b0bec5" />
                                  <XAxis dataKey="index" type="number" domain={[1, Math.max(1, trainingDisplayChartData.length)]} tick={false} axisLine={{ stroke: '#263238', strokeWidth: 2 }} tickLine={false} label={{ value: 'Skills', position: 'bottom', offset: 8, style: { fontSize: 12, fontWeight: 600, fill: '#263238' } }} />
                                  <YAxis domain={[1, 5]} allowDecimals={false} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 12, fill: '#263238', fontWeight: 600 }} axisLine={{ stroke: '#263238', strokeWidth: 2 }} tickLine={{ stroke: '#263238', strokeWidth: 1.5 }} label={{ value: 'Rating', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: '#263238', textAnchor: 'middle' } }} />
                                  <Tooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload?.length) return null;
                                      const data = payload[0]?.payload;
                                      return (
                                        <Paper sx={{ p: 1.5, maxWidth: 360 }} elevation={3}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Skill</Typography>
                                          <Typography variant="body2" sx={{ mb: 1 }}>{data?.fullLabel ?? ''}</Typography>
                                          {payload.map((p) => (
                                            <Typography key={p.dataKey} variant="body2" sx={{ color: p.color, fontWeight: 500 }}>{p.name}: {p.value}</Typography>
                                          ))}
                                        </Paper>
                                      );
                                    }}
                                  />
                                  <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(value) => <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>} />
                                  <Line type="monotone" dataKey="r0" name={selectedTrainingBenchmarkRows[0]?.playerName + (selectedTrainingBenchmarkRows[0]?.playerId === player?.id ? ' (You)' : '') ?? '1'} stroke="#1565c0" strokeWidth={2.5} dot={{ r: 5, fill: '#1565c0', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                                  <Line type="monotone" dataKey="r1" name={selectedTrainingBenchmarkRows[1]?.playerName + (selectedTrainingBenchmarkRows[1]?.playerId === player?.id ? ' (You)' : '') ?? '2'} stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 5, fill: '#2e7d32', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                                </LineChart>
                              </ResponsiveContainer>
                            </Box>
                          </Paper>
                          {trainingDisplayTableRows.length > 0 && (
                            <Paper sx={{ overflow: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ maxWidth: 360 }}><strong>Skill</strong></TableCell>
                                    <TableCell align="center"><strong>{selectedTrainingBenchmarkRows[0]?.playerName}{selectedTrainingBenchmarkRows[0]?.playerId === player?.id ? ' (You)' : ''}</strong></TableCell>
                                    <TableCell align="center"><strong>{selectedTrainingBenchmarkRows[1]?.playerName}{selectedTrainingBenchmarkRows[1]?.playerId === player?.id ? ' (You)' : ''}</strong></TableCell>
                                    <TableCell align="center"><strong>Trend</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {trainingDisplayTableRows.map((row, i) => (
                                    <TableRow key={i}>
                                      <TableCell sx={{ maxWidth: 360 }} title={row.name}>{row.name}</TableCell>
                                      <TableCell align="center">{row.ratings[0] != null ? row.ratings[0] : 'N/A'}</TableCell>
                                      <TableCell align="center">{row.ratings[1] != null ? row.ratings[1] : 'N/A'}</TableCell>
                                      <TableCell align="center">
                                        {row.ratings[0] != null && row.ratings[1] != null ? (
                                          row.ratings[1] > row.ratings[0] ? <ArrowUpIcon color="success" fontSize="small" />
                                          : row.ratings[1] < row.ratings[0] ? <ArrowDownIcon color="error" fontSize="small" />
                                          : <RemoveIcon color="action" fontSize="small" />
                                        ) : '–'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          )}
                        </>
                      ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>No skill-wise data available for these two evaluations. They may not have the same skill items.</Alert>
                      )}
                    </Box>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Sport</InputLabel>
                    <Select value={sport} label="Sport" onChange={(e) => setSport(e.target.value)}>
                      <MenuItem value="Badminton">Badminton</MenuItem>
                      <MenuItem value="Cricket">Cricket</MenuItem>
                      <MenuItem value="Tennis">Tennis</MenuItem>
                      <MenuItem value="Football">Football</MenuItem>
                      <MenuItem value="Athletics">Athletics</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={categoryId} label="Category" onChange={(e) => setCategoryId(e.target.value)}>
                      <MenuItem value="coach-academy">{CATEGORY_LABELS['coach-academy']}</MenuItem>
                      <MenuItem value="practical-test">{CATEGORY_LABELS['practical-test']}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Period</InputLabel>
                    <Select value={periodKey} label="Period" onChange={(e) => setPeriodKey(e.target.value)}>
                      {myReports.map((r) => (<MenuItem key={r.reportId} value={r.periodKey}>{r.periodKey}</MenuItem>))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={loadComparison} disabled={loadingCompare || !periodKey}>
                    {loadingCompare ? <CircularProgress size={24} /> : 'Compare Performance'}
                  </Button>
                </Box>
              </Paper>
              {(comparison.length > 0) && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Select <strong>two</strong> rows below to see <strong>skill-wise comparison</strong> (line graph and table by stroke/skill, not average).</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>Compare</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>%</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparison.map((row, idx) => {
                          const gradeColor = row.grade && (String(row.grade).startsWith('A') || row.grade === 'A+') ? 'success' : row.grade && String(row.grade).startsWith('B') ? 'primary' : 'default';
                          return (
                            <TableRow key={row.reportId} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedForBenchmark.includes(row.reportId)}
                                  onChange={() => toggleBenchmark(row.reportId)}
                                  disabled={selectedForBenchmark.length >= 2 && !selectedForBenchmark.includes(row.reportId)}
                                />
                              </TableCell>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{row.playerName}{row.playerId === player?.id ? ' (You)' : ''}</TableCell>
                              <TableCell>{row.score}</TableCell>
                              <TableCell><Chip label={row.grade} size="small" color={gradeColor} /></TableCell>
                              <TableCell>{String(row.percentage) + '%'}</TableCell>
                              <TableCell><Button size="small" variant="outlined" onClick={() => openRankingReportView(row.reportId, row.playerId, row.playerName + (row.playerId === player?.id ? ' (You)' : ''))}>View</Button></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {selectedForBenchmark.length === 2 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Skill-wise comparison</Typography>
                      {selectedBenchmarkReports.some((r) => loadingRankingDetails[r.reportId]) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading report details for skill-wise graph…</Typography>
                        </Box>
                      ) : benchmarkDetails.length === 2 ? (
                        displayChartData.length > 0 ? (
                          <>
                            <Paper sx={{ p: 2, mb: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rating by skill (stroke)</Typography>
                                <FormControlLabel
                                  control={<Checkbox checked={onlyStrokesWithDelta} onChange={(e) => setOnlyStrokesWithDelta(e.target.checked)} size="small" />}
                                  label={<Typography variant="body2">Show only skills with change</Typography>}
                                />
                              </Box>
                              <Box sx={{ bgcolor: '#f5f9fc', borderRadius: 1, p: 1 }}>
                                <ResponsiveContainer width="100%" height={300}>
                                  <LineChart data={displayChartData} margin={{ top: 10, right: 24, left: 8, bottom: 32 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#b0bec5" />
                                    <XAxis dataKey="index" type="number" domain={[1, Math.max(1, displayChartData.length)]} tick={false} axisLine={{ stroke: '#263238', strokeWidth: 2 }} tickLine={false} label={{ value: 'Skills (strokes)', position: 'bottom', offset: 8, style: { fontSize: 12, fontWeight: 600, fill: '#263238' } }} />
                                    <YAxis domain={[1, 5]} allowDecimals={false} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 12, fill: '#263238', fontWeight: 600 }} axisLine={{ stroke: '#263238', strokeWidth: 2 }} tickLine={{ stroke: '#263238', strokeWidth: 1.5 }} label={{ value: 'Rating', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: '#263238', textAnchor: 'middle' } }} />
                                    <Tooltip
                                      content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const data = payload[0]?.payload;
                                        const strokeLabel = data?.fullLabel ?? '';
                                        return (
                                          <Paper sx={{ p: 1.5, maxWidth: 360 }} elevation={3}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Skill / Stroke</Typography>
                                            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{strokeLabel}</Typography>
                                            {payload.map((p) => (
                                              <Typography key={p.dataKey} variant="body2" sx={{ color: p.color, fontWeight: 500 }}>{p.name}: {p.value}</Typography>
                                            ))}
                                          </Paper>
                                        );
                                      }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(value) => <span style={{ fontWeight: 600, color: '#333' }}>{value}</span>} />
                                    <Line type="monotone" dataKey="r0" name={benchmarkDetails[0]?.playerName ?? '1'} stroke="#1565c0" strokeWidth={2.5} dot={{ r: 5, fill: '#1565c0', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                                    <Line type="monotone" dataKey="r1" name={benchmarkDetails[1]?.playerName ?? '2'} stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 5, fill: '#2e7d32', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </Box>
                            </Paper>
                            <Paper sx={{ overflow: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ maxWidth: 360 }}><strong>Skill / Stroke</strong></TableCell>
                                    <TableCell align="center"><strong>{benchmarkDetails[0]?.playerName ?? '1'}</strong></TableCell>
                                    <TableCell align="center"><strong>{benchmarkDetails[1]?.playerName ?? '2'}</strong></TableCell>
                                    <TableCell align="center"><strong>Trend</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {displayTableRows.map((row, i) => (
                                    <TableRow key={i}>
                                      <TableCell sx={{ maxWidth: 360 }} title={row.displayLabel}>{row.displayLabel || row.code}</TableCell>
                                      <TableCell align="center">{row.ratings[0] != null ? row.ratings[0] : 'N/A'}</TableCell>
                                      <TableCell align="center">{row.ratings[1] != null ? row.ratings[1] : 'N/A'}</TableCell>
                                      <TableCell align="center">
                                        {row.ratings[0] != null && row.ratings[1] != null ? (
                                          row.ratings[1] > row.ratings[0] ? <ArrowUpIcon color="success" fontSize="small" />
                                          : row.ratings[1] < row.ratings[0] ? <ArrowDownIcon color="error" fontSize="small" />
                                          : <RemoveIcon color="action" fontSize="small" />
                                        ) : '–'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Paper>
                          </>
                        ) : (
                          <Alert severity="info" sx={{ mt: 2 }}>No skill-wise (stroke) data available for these two reports. The reports may not have per-skill ratings loaded yet.</Alert>
                        )
                      ) : null}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* Ranking report view (in-page, no navigate) */}
      <Dialog open={reportViewDialog === 'ranking'} onClose={closeReportView} maxWidth="md" fullWidth>
        <DialogTitle>Report view</DialogTitle>
        <DialogContent>
          {reportViewPayload?.reportId && (() => {
            const detail = rankingDetailCache[reportViewPayload.reportId];
            if (!detail) {
              return (
                <Box sx={{ py: 2 }}><Typography color="text.secondary">Loading report…</Typography></Box>
              );
            }
            const categoryLabel = CATEGORY_LABELS[categoryId] || categoryId;
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{detail.playerName}</Typography>
                <Typography variant="body2" color="text.secondary">Period: {periodKey} · Category: {categoryLabel}</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Skill / Stroke</strong></TableCell><TableCell align="right"><strong>Rating</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {detail.strokes.map((s: StrokeWithMeta, i: number) => (
                        <TableRow key={i}><TableCell>{s.displayLabel || s.code}</TableCell><TableCell align="right">{s.rating != null ? s.rating : 'N/A'}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={closeReportView}>Close</Button></DialogActions>
      </Dialog>

      {/* Training report view (in-page, no navigate) */}
      <Dialog open={reportViewDialog === 'training'} onClose={closeReportView} maxWidth="md" fullWidth>
        <DialogTitle>Training report view</DialogTitle>
        <DialogContent>
          {reportViewPayload?.evaluationId && (() => {
            const full = trainingDetailCache[reportViewPayload.evaluationId];
            if (!full) {
              return (
                <Box sx={{ py: 2 }}><Typography color="text.secondary">Report data not available. Compare Performance first.</Typography></Box>
              );
            }
            const items = full.items || [];
            const byCategory: Record<string, { name: string; rating: number | null }[]> = {};
            items.forEach((item: any) => {
              const cat = item.category || 'Other';
              if (!byCategory[cat]) byCategory[cat] = [];
              byCategory[cat].push({ name: item.name || '', rating: item.notApplicable ? null : (item.rating ?? null) });
            });
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{reportViewPayload.playerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Period: {full.period || '–'} · Average: {(full.averageRating != null ? full.averageRating : 0).toFixed(2)}/5
                </Typography>
                {Object.entries(byCategory).map(([cat, rows]) => (
                  <Box key={cat} sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{cat}</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}><TableCell><strong>Skill</strong></TableCell><TableCell align="right"><strong>Rating</strong></TableCell></TableRow></TableHead>
                        <TableBody>
                          {rows.map((r, i) => (
                            <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell align="right">{r.rating != null ? r.rating : 'N/A'}</TableCell></TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={closeReportView}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComparePeersPage;
